import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import Confetti from "react-confetti";
import { generateProblem, calculateProgressGain } from "./elo";
import ProgressBar from "./components/ProgressBar";
import { useAuth } from "./context/AuthContext";
import { useGeld } from "./context/GeldContext"; // Import the useGeld hook
import { useNavigate, useLocation } from "react-router-dom";
import UnitDropdown from "./components/UnitDropdown";
import HintBox from "./components/HintBox";
import TimingDisplay from "./components/TimingDisplay";
import { buttonStyle, circleButtonStyle } from "./utils/styleConstants";
import { getUnitName, unitsList } from "./utils/unitHelpers";
import { isCanvasBlank } from "./utils/canvasHelpers";
import useCanvas from "./hooks/useCanvas";
import useAudio from "./hooks/useAudio";
import useHintSystem from "./hooks/useHintSystem";
import ProblemCard from "./components/ProblemCard"; // Import the new component
import LinksAvatar from "./components/LinksAvatar"; // Import the new component
import AnswerBox from "./components/AnswerBox"; // Add this import
import TrialModal from "./components/TrialModal";
import WelcomeBanner from "./components/WelcomeBanner";
import GeldTracker from "./components/GeldTracker"; // Import the GeldTracker component
import GeldTransaction from "./components/GeldTransaction"; // Import the GeldTransaction component
import StreakCounter from "./components/StreakCounter"; // Add this import
import ConfirmationModal from "./components/ConfirmationModal"; // Import the confirmation modal
import ExpandedProgressBar from "./components/ExpandedProgressBar"; // Import the new component
import BorderLayout from "./components/BorderLayout";

function MathApp({ mode = "learn" }) {
  const { user, updateUserData, signOut } = useAuth();
  const { addGeld } = useGeld(); // Get the addGeld function from context
  const navigate = useNavigate();
  const location = useLocation();

  // Add trial mode state
  const [trialProblemsCount, setTrialProblemsCount] = useState(0);
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);

  // Add state for confirmation modal
  const [showClearConfirmation, setShowClearConfirmation] = useState(false);

  // Determine if we're in trial mode (no user logged in)
  const isTrialMode = !user && mode !== "trial-ended";

  // Parse unit from URL if available
  const queryParams = new URLSearchParams(location.search);
  const unitParam = queryParams.get("unit");

  // State variables for math problems and answers
  // Load stored problem from localStorage
  const storedProblem = localStorage.getItem("practiceProblem");
  const initialProblem = storedProblem
    ? JSON.parse(storedProblem)
    : { display: "10 + 4", eval: "10 + 4" };
  const [problem, setProblem] = useState(initialProblem);

  // Add a ref to track if initial problem has been generated
  const initialProblemGenerated = useRef(false);
  // Add ref to track if unit progress has been loaded
  const progressLoaded = useRef(false);

  const [userAnswer, setUserAnswer] = useState("");
  const [wrongAttempts, setWrongAttempts] = useState(0);
  const [isWrongAnswer, setIsWrongAnswer] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [totalCorrect, setTotalCorrect] = useState(0);
  const [hintBoxBottom, setHintBoxBottom] = useState("10px");

  // Unit-related state
  const [unitNumber, setUnitNumber] = useState(parseInt(unitParam) || 1);
  const [unitProgress, setUnitProgress] = useState(0);
  const [progressKey, setProgressKey] = useState(Date.now()); // For smooth progress updates

  // Add state to store mastered units
  const [masteredUnits, setMasteredUnits] = useState([]);

  // Add timing states before callbacks that reference them
  const [problemStartTime, setProblemStartTime] = useState(Date.now());

  // Debugging state
  const [aiDebug, setAiDebug] = useState("");
  const [sessionId, setSessionId] = useState("");

  // UI state
  const dropdownRef = useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Add state for editing mode
  // Load editing state from localStorage
  const storedIsEditing = localStorage.getItem("isEditing") === "true";
  const [isEditing, setIsEditing] = useState(storedIsEditing);

  // Load number states from localStorage
  const storedNum1 = localStorage.getItem("num1") || 0;
  const storedNum2 = localStorage.getItem("num2") || 0;
  const storedOperator = localStorage.getItem("operator") || "+";

  const [num1, setNum1] = useState(parseFloat(storedNum1));
  const [num2, setNum2] = useState(parseFloat(storedNum2));
  const [operator, setOperator] = useState(storedOperator);
  const [showOperators, setShowOperators] = useState(false);

  useEffect(() => {
    if (problem && problem.display) {
      // Extract numbers and operator from the problem string
      const parts = problem.display
        .split(/([+\-*/×÷])/)
        .map((part) => part.trim());
      if (parts.length === 3) {
        setNum1(parseFloat(parts[0]));
        setOperator(parts[1]);
        setNum2(parseFloat(parts[2]));
      }
    }
  }, [problem]);

  // Import custom hooks
  const {
    canvasRef,
    isDrawing,
    currentTool,
    setCurrentTool,
    handlePointerDown,
    draw,
    stopDrawing,
    clearCanvas,
    undoLastStroke, // Use the undo function from useCanvas
    strokesRef,
    currentStrokeRef,
    hasDrawings, // Use the hasDrawings state from useCanvas
  } = useCanvas();

  const {
    audioPermission,
    hintActive,
    setHintActive,
    isClarificationPlaying,
    setIsClarificationPlaying,
    playAudio,
    stopAudio,
    hintAudioRef,
    audioError,
  } = useAudio();

  // Initialize useHintSystem with problem, playAudio, and state setters
  const {
    hint,
    setHint,
    hintCount,
    setHintCount,
    timings,
    hintBoxRef,
    hintClosing,
    showClarificationButton,
    isHintLoading,
    requestHint: baseRequestHint,
    requestClarification: baseRequestClarification,
    handleCloseHint: baseHandleCloseHint,
  } = useHintSystem(
    problem,
    playAudio,
    setHintActive,
    setIsClarificationPlaying
  );

  // Generate a new problem function - extract this logic to reuse
  const generateNewProblem = useCallback(
    (newProblem) => {
      console.log(
        "Generating new problem:",
        newProblem ? "Custom problem" : "Random problem"
      );

      if (newProblem) {
        setProblem(newProblem);
        // Update num1, num2, and operator based on the new problem
        const parts = newProblem.display
          .split(/([+\-*/×÷])/)
          .map((part) => part.trim());
        if (parts.length === 3) {
          setNum1(parseFloat(parts[0]));
          setOperator(parts[1]);
          setNum2(parseFloat(parts[2]));
        }
      } else {
        // Generate a new problem directly based on user progress without multiple attempts
        const generatedProblem = generateProblem(unitNumber, unitProgress);
        setProblem(
          typeof generatedProblem === "string"
            ? { display: generatedProblem, eval: generatedProblem }
            : generatedProblem
        );
        if (typeof generatedProblem !== "string") {
          const parts = generatedProblem.display
            .split(/([+\-*/÷])/)
            .map((part) => part.trim());
          if (parts.length === 3) {
            setNum1(parseFloat(parts[0]));
            setOperator(parts[1]);
            setNum2(parseFloat(parts[2]));
          }
        }
      }

      setSessionId(Math.random().toString(36).substring(7));
      setProblemStartTime(Date.now());
      setHintCount(0);
    },
    [
      unitNumber,
      unitProgress,
      problem,
      setProblem,
      generateProblem,
      setSessionId,
      setProblemStartTime,
      setHintCount,
    ]
  );

  const handleSave = useCallback(() => {
    const newNum1 = num1 || 0; // Default to 0 if empty
    const newNum2 = num2 || 0; // Default to 0 if empty
    let newDisplay = `${newNum1} ${operator} ${newNum2}`;
    let evalDisplay = newDisplay;
    // Replace "÷" with "/" and "×" with "*" for correct evaluation
    evalDisplay = evalDisplay.replace("÷", "/").replace("×", "*");
    let newEval;
    try {
      newEval = eval(evalDisplay);
    } catch (error) {
      alert("Invalid expression!");
      return;
    }
    const newProblem = {
      display: newDisplay,
      eval: evalDisplay,
    };
    generateNewProblem(newProblem);
    setIsEditing(false);
  }, [num1, num2, operator, generateNewProblem, setIsEditing]);

  const handleOperatorClick = (op) => {
    setOperator(op);
    setShowOperators(false);
  };

  // Add state for timing problems
  const [streakCount, setStreakCount] = useState(0);

  // Load the user's progress for the selected unit

  useEffect(() => {
    if (user) {
      const unitProgressField = `unit_${unitNumber}_progress`;
      const currentProgress = user[unitProgressField];
      setUnitProgress(currentProgress);
      progressLoaded.current = true; // Mark that progress has been loaded

      // Check if this unit should be accessible
      const isPreviousUnitCompleted =
        unitNumber === 1 || user[`unit_${unitNumber - 1}_progress`] >= 80;

      if (!isPreviousUnitCompleted && mode === "learn") {
        // Redirect to dashboard if trying to access a locked unit
        navigate("/");
      }

      // Determine mastered units
      const mastered = [];
      for (let i = 1; i <= 4; i++) {
        if ((user[`unit_${i}_progress`] || 0) >= 80) {
          mastered.push(i);
        }
      }
      setMasteredUnits(mastered);
    }
  }, [user, unitNumber, navigate, mode]);

  // Add a new function to generate the first problem that respects difficulty
  const generateFirstProblem = useCallback(async () => {
    // Only run if user exists
    if (!user) return;
    setIsLoading(true);
    const latestProgress = user[`unit_${unitNumber}_progress`] || 0;
    setUnitProgress(latestProgress);
    // Generate problem using the latest progress value
    const newProblem = generateProblem(unitNumber, latestProgress);
    setProblem(
      typeof newProblem === "string"
        ? { display: newProblem, eval: newProblem }
        : newProblem
    );
    setSessionId(Math.random().toString(36).substring(7));
    setProblemStartTime(Date.now());
    setHintCount(0);
    initialProblemGenerated.current = true;
    setIsLoading(false);
  }, [
    user,
    unitNumber,
    generateProblem,
    setSessionId,
    setProblemStartTime,
    setHintCount,
  ]);

  // Consolidated useEffect for initial problem generation
  useEffect(() => {
    // Skip if we've already generated the initial problem
    if (initialProblemGenerated.current) return;

    if (user) {
      // For logged-in users: only generate after progress is loaded
      if (progressLoaded.current) {
        generateFirstProblem();
      }
    } else if (mode !== "trial-ended") {
      // For trial mode: generate immediately without waiting for progress
      setIsLoading(true);
      const newProblem = generateProblem(unitNumber, 0); // Start with zero progress
      setProblem(
        typeof newProblem === "string"
          ? { display: newProblem, eval: newProblem }
          : newProblem
      );
      setSessionId(Math.random().toString(36).substring(7));
      setProblemStartTime(Date.now());
      setHintCount(0);
      initialProblemGenerated.current = true;
      setIsLoading(false);
    }
  }, [
    user,
    progressLoaded.current,
    unitNumber,
    mode,
    generateFirstProblem,
    generateProblem,
    setSessionId,
    setProblemStartTime,
    setHintCount,
  ]);

  // Navigation handlers
  const handleBackToDashboard = () => {
    navigate("/");
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate("/login");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  // Wrap the hint system functions to provide canvas reference
  const requestHint = async () => {
    try {
      const result = await baseRequestHint(canvasRef.current);
      // Debug log to check the audio URL format
      if (result && result.audioUrl) {
        console.log("Received audio URL:", result.audioUrl);
      }
    } catch (error) {
      console.error("Error requesting hint:", error);
    }
  };

  const requestClarification = async () => {
    await baseRequestClarification(canvasRef.current);
  };

  const handleCloseHint = () => {
    baseHandleCloseHint(stopAudio);
  };
  // Enhanced clear canvas that also clears hint
  const handleClearCanvas = () => {
    setShowClearConfirmation(true);
  };

  // Function to actually clear the canvas after confirmation
  const confirmClearCanvas = () => {
    clearCanvas();
    stopAudio();
    setHint("");
    setHintActive(false);
    setIsClarificationPlaying(false);
    setShowClearConfirmation(false);
  };

  // New function to clear canvas directly without confirmation
  const clearCanvasWithoutConfirmation = () => {
    clearCanvas();
    stopAudio();
    setHint("");
    setHintActive(false);
    setIsClarificationPlaying(false);
  };

  // Function to cancel clearing
  const cancelClearCanvas = () => {
    setShowClearConfirmation(false);
  };

  const handleCheckCanvas = () => {
    const canvas = canvasRef.current;
    console.log("DEBUG: Checking canvas blank status:", isCanvasBlank(canvas));
  };

  // Update unit progress in the database
  const updateProgress = async (newProgress) => {
    if (!user || mode === "practice") return; // Prevent progress updates in practice mode

    // Make sure progress doesn't decrease and is capped at 100
    // This ensures we stay at max difficulty once we reach 100%
    newProgress = Math.min(100, Math.max(unitProgress, newProgress));

    if (newProgress === unitProgress) return;

    try {
      // Create an update object for the specific unit progress
      const updateObj = { [`unit_${unitNumber}_progress`]: newProgress };

      // Update local state with the new progress value
      setUnitProgress(newProgress);

      // Use a new key for the progress bar
      setProgressKey(Date.now());

      // Update database
      await updateUserData(updateObj);
    } catch (error) {
      console.error("Failed to update progress:", error);
    }
  };

  // Persist state to localStorage on changes
  useEffect(() => {
    localStorage.setItem("practiceProblem", JSON.stringify(problem));
    localStorage.setItem("isEditing", isEditing);
    localStorage.setItem("num1", num1);
    localStorage.setItem("num2", num2);
    localStorage.setItem("operator", operator);
  }, [problem, isEditing, num1, num2, operator]);

  // Replace single confetti state with array of confetti instances
  const [confettiInstances, setConfettiInstances] = useState([]);

  // Function to add a new confetti celebration
  const triggerConfetti = () => {
    const id = Date.now(); // Use timestamp as unique ID

    // Add new confetti instance to the array
    setConfettiInstances((prev) => [
      ...prev,
      {
        id,
        position: {
          x: window.innerWidth / 2,
          y: 30,
          w: 0,
          h: 0,
        },
      },
    ]);

    // Remove this confetti instance after animation completes
    setTimeout(() => {
      setConfettiInstances((prev) =>
        prev.filter((instance) => instance.id !== id)
      );
    }, 3000);
  };

  const [isCorrectAnswer, setIsCorrectAnswer] = useState(false);

  // Add audio ref for success ding
  const successAudioRef = useRef(new Audio("/audio/ding.mp3"));
  // Set a lower volume (0.3 is 30% of maximum volume)
  successAudioRef.current.volume = 0.3;

  // Handle answer submission - use the generateNewProblem function
  const handleAnswerSubmit = async () => {
    try {
      const correctAnswer = eval(problem.eval || problem);
      const isCorrect = parseFloat(userAnswer) === correctAnswer;

      // Calculate time taken to solve this problem
      const timeTakenSeconds = (Date.now() - problemStartTime) / 1000;

      if (isCorrect) {
        // Play success sound when answer is correct
        successAudioRef.current.currentTime = 0; // Reset audio to start
        successAudioRef.current.play();

        // Calculate geld reward based on unit, speed, and streak
        let geldReward = 1; // Base reward

        // Bonus for higher units (more difficult problems)
        geldReward += Math.min(unitNumber - 1, 3);

        // Bonus for fast solutions
        if (timeTakenSeconds < 5) geldReward += 2;
        else if (timeTakenSeconds < 10) geldReward += 1;

        // Streak bonus
        if (streakCount >= 5) geldReward += 2;
        else if (streakCount >= 3) geldReward += 1;

        // Award geld - only if user is logged in
        if (user) {
          addGeld(geldReward, "correct_answer");
        }

        // Visual feedback first - trigger confetti without state blocking
        triggerConfetti();
        setIsWrongAnswer(false);
        setIsCorrectAnswer(true); // Set the correct answer state

        // Update streak count for consecutive correct answers
        setStreakCount((prevStreak) => prevStreak + 1);

        // Reset states
        setUserAnswer("");
        setHintActive(false);
        setIsClarificationPlaying(false);
        setWrongAttempts(0);

        // Clear canvas directly without confirmation
        clearCanvasWithoutConfirmation();

        // Handle trial mode counting
        if (isTrialMode) {
          const newCount = trialProblemsCount + 1;
          setTrialProblemsCount(newCount);

          // Check if trial limit reached
          if (newCount >= 5) {
            setIsFadingOut(true);

            // After fade out animation, show login modal
            setTimeout(() => {
              setShowTrialModal(true);
            }, 1000);

            return; // Don't continue with normal flow
          }
        }

        // Calculate progress gain with our enhanced formula
        // We now pass time taken and the difficulty factor from the problem
        const progressGain = calculateProgressGain(
          isCorrect,
          hintCount,
          timeTakenSeconds,
          problem.difficultyFactor || 1
        );

        // Apply smaller streak bonus for consistent performance
        // Reduced from 0.5 per streak to 0.1
        const streakBonus = Math.min(streakCount, 10) * 0.1;

        // Apply total gain, ensuring minimum progress
        const totalGain = Math.max(0.1, progressGain + streakBonus);

        // Generate next problem immediately but don't set it yet
        const newProgress = Math.min(100, unitProgress + totalGain);

        // Feedback about progress gain - show with one decimal place
        setFeedback(`+${totalGain.toFixed(1)} progress!`);

        // Generate a new problem immediately
        generateNewProblem();

        // Update total correct count
        setTotalCorrect((prev) => prev + 1);

        // Update progress in database
        await updateProgress(newProgress);

        // Reset the correct answer state after a delay
        setTimeout(() => {
          setIsCorrectAnswer(false);
        }, 2000);
      } else {
        // Reset streak on wrong answers
        setStreakCount(0);
        setIsCorrectAnswer(false); // Ensure correct answer state is false

        // Add a small progress penalty for incorrect answers beyond the first attempt
        if (wrongAttempts > 0 && unitProgress > 0) {
          // Small penalty that increases with subsequent wrong attempts
          const penalty = Math.min(0.2 * wrongAttempts, 1.0);
          const newProgress = Math.max(0, unitProgress - penalty);

          if (newProgress < unitProgress) {
            setFeedback(`-${penalty.toFixed(1)} progress`);
            await updateProgress(newProgress);
          }
        }

        // Handle incorrect answer
        setWrongAttempts(wrongAttempts + 1);
        setIsWrongAnswer(true);
        setUserAnswer("");
        setTimeout(() => setIsWrongAnswer(false), 500);
      }
    } catch (error) {
      console.error("Error checking answer:", error);
      setUserAnswer("");
    }
  };

  // Add state for hint button animation
  const [isHintButtonShaking, setIsHintButtonShaking] = useState(false);
  const inactivityTimerRef = useRef(null);

  // Function to reset inactivity timer
  const resetInactivityTimer = useCallback(() => {
    // Clear any existing timer
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    // Stop shaking if it was shaking
    setIsHintButtonShaking(false);

    // Set new timer for 10 seconds
    inactivityTimerRef.current = setTimeout(() => {
      // Start shaking after 10 seconds of inactivity
      setIsHintButtonShaking(true);
    }, 10000);
  }, []);

  // Reset the timer when component mounts
  useEffect(() => {
    resetInactivityTimer();

    return () => {
      // Clean up the timer when component unmounts
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [resetInactivityTimer]);

  // Reset inactivity timer when a new problem is generated
  useEffect(() => {
    resetInactivityTimer();
  }, [problem, resetInactivityTimer]);

  // Modify the draw function to reset the inactivity timer
  const handleDraw = (e) => {
    draw(e);
    // Reset the inactivity timer whenever the user draws
    resetInactivityTimer();
  };

  // Add state for expanded progress bar
  const [isProgressExpanded, setIsProgressExpanded] = useState(false);

  // Modify the handleProgressBarClick to ensure it gets the event object
  const handleProgressBarClick = (e) => {
    if (e) e.stopPropagation(); // Ensure event doesn't bubble up
    setIsProgressExpanded(true);
  };

  // Handler for closing the expanded view
  const handleCloseExpanded = () => {
    setIsProgressExpanded(false);
  };

  const [isLoading, setIsLoading] = useState(false); // NEW loading state

  return (
    <BorderLayout>
      <div
        className="App"
        style={{
          height: "100%",
          width: "100%",
          overflow: "hidden",
          position: "relative",
          fontFamily: "'Inter', sans-serif",
          opacity: isFadingOut ? 0.3 : 1,
          transition: "opacity 1s ease",
        }}
      >
        {/* Add CSS animation keyframes with improved rotational shake */}
        <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translate(-50%, -10px); }
          to { opacity: 1; transform: translate(-50%, 0); }
        }
        @keyframes fadeOut {
          from { opacity: 1; transform: translate(-50%, 0); }
          to { opacity: 0; transform: translate(-50%, -10px); }
        }
        @keyframes rotationalShake {
          0% { transform: rotate(0deg); }
          10% { transform: rotate(-10deg); }
          20% { transform: rotate(8deg); }
          30% { transform: rotate(-6deg); }
          40% { transform: rotate(4deg); }
          50% { transform: rotate(-2deg); }
          60% { transform: rotate(1deg); }
          70% { transform: rotate(-0.5deg); }
          80% { transform: rotate(0.25deg); }
          90% { transform: rotate(-0.1deg); }
          100% { transform: rotate(0deg); }
        }
      `}</style>

        {/* Render all active confetti instances */}
        {confettiInstances.map((instance) => (
          <Confetti
            key={instance.id}
            numberOfPieces={200}
            recycle={false}
            gravity={0.3}
            confettiSource={instance.position}
          />
        ))}

        {/* Back button and geld tracker */}
        <div
          style={{
            position: "fixed",
            top: "20px",
            left: "20px",
            zIndex: 10,
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <button
            onClick={handleBackToDashboard}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "10px",
              backgroundColor: "#f3e1b2",
              border: "none",
              borderRadius: "12px",
              cursor: "pointer",
              fontWeight: "600",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
          >
            <span style={{ marginRight: "5px" }}>←</span> Back
          </button>

          {/* Add GeldTracker next to back button if user is logged in */}
          {user && <GeldTracker />}
        </div>

        {/* Canvas */}
        {user && (
          <canvas
            ref={canvasRef}
            onPointerDown={(e) => {
              handlePointerDown(e);
              resetInactivityTimer();
            }}
            onPointerMove={handleDraw}
            onPointerUp={stopDrawing}
            onPointerLeave={stopDrawing}
            onTouchStart={(e) => {
              handlePointerDown(e);
              resetInactivityTimer();
            }}
            onTouchMove={handleDraw}
            onTouchEnd={stopDrawing}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              zIndex: 1,
              touchAction: "none",
              WebkitTouchCallout: "none",
              WebkitUserSelect: "none",
              userSelect: "none",
              backgroundColor: "transparent",
            }}
          />
        )}

        {/* Hint Box - Updated to separate isHintLoading from isClarificationPlaying */}
        <HintBox
          hint={hint}
          hintBoxRef={hintBoxRef}
          hintClosing={hintClosing}
          handleCloseHint={handleCloseHint}
          showClarificationButton={showClarificationButton}
          requestClarification={requestClarification}
          isClarificationPlaying={isClarificationPlaying}
          isHintLoading={isHintLoading}
          hintBoxBottom={hintBoxBottom}
          audioError={audioError}
        />

        {/* Timing Display */}
        {/* <TimingDisplay timings={timings} /> */}

        {/* Problem Display & Progress Bar */}
        <div
          style={{
            position: "fixed",
            top: window.innerWidth < 768 ? "70px" : "20px", // Move down on mobile screens
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "95%",
            maxWidth: "400px",
          }}
        >
          {/* Progress Bar - Add user and updateUserData props */}
          {mode !== "practice" && ( // Conditionally render progress bar
            <div
              style={{
                width: "100%",
                marginBottom: "20px",
                cursor: "pointer", // Add cursor pointer to indicate it's clickable
              }}
              onClick={(e) => {
                e.stopPropagation(); // Stop propagation here
                handleProgressBarClick(e);
              }}
            >
              <ProgressBar
                key={progressKey}
                progress={unitProgress}
                unitNumber={unitNumber}
                user={user}
                updateUserData={updateUserData}
                isClickable={true} // Pass prop to indicate it's clickable
                onProgressBarClick={handleProgressBarClick} // Pass the click handler
              />
            </div>
          )}

          {/* Practice mode indicator */}
          {mode === "practice" && (
            <div
              style={{
                color: "#555",
                padding: "6px 12px",
                borderRadius: "8px",
                fontWeight: "700", // Make it bolder
                fontSize: "22px", // Make it bigger
                marginBottom: "15px",
                fontFamily: "'Inter', sans-serif", // Use the correct font
              }}
            >
              Practice Mode
            </div>
          )}

          {/* Problem card without streak counter */}
          {isLoading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "200px",
                width: "100%",
              }}
            >
              {/* Simple loading indicator */}
              <p>Loading...</p>
            </div>
          ) : (
            <ProblemCard
              mode={mode}
              isEditing={isEditing}
              problem={problem}
              num1={num1}
              num2={num2}
              operator={operator}
              showOperators={showOperators}
              setNum1={setNum1}
              setNum2={setNum2}
              setOperator={setOperator}
              setShowOperators={setShowOperators}
              handleSave={handleSave}
              buttonStyle={buttonStyle}
              generateNewProblem={generateNewProblem}
              setIsEditing={setIsEditing}
            />
          )}
        </div>

        {/* Move StreakCounter outside problem card area */}
        <StreakCounter
          streakCount={streakCount}
          isCorrectAnswer={isCorrectAnswer}
        />

        {/* Drawing Tools */}
        <div
          style={{
            position: "fixed",
            top: window.innerWidth < 800 ? "auto" : "20px",
            bottom: window.innerWidth < 800 ? "100px" : "auto",
            right: "20px",
            zIndex: 2,
            display: "flex",
            flexDirection: "column", // Stack buttons vertically
            gap: "10px",
          }}
        >
          {/* Standard drawing tools in a row */}
          <div
            style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}
          >
            <button
              style={{
                ...circleButtonStyle,
                background:
                  currentTool === "draw"
                    ? "rgba(156, 218, 237, 0.9)"
                    : "#f3e1b2",
              }}
              onClick={() => setCurrentTool("draw")}
            >
              ✏️
            </button>
            <button
              style={{
                ...circleButtonStyle,
                background:
                  currentTool === "erase"
                    ? "rgba(156, 218, 237, 0.9)"
                    : "#f3e1b2",
              }}
              onClick={() => setCurrentTool("erase")}
            >
              <i
                className="fas fa-eraser"
                style={{
                  fontSize: window.innerWidth < 800 ? "30px" : "45px",
                  color: currentTool === "erase" ? "#ffb6c1" : "#ffb6c1",
                }}
              ></i>
            </button>
          </div>

          {/* Undo and clear buttons - only show if there are drawings */}
          {hasDrawings && (
            <div
              style={{
                display: "flex",
                gap: "10px",
                justifyContent: "flex-end",
              }}
            >
              <button
                title="Undo Last Stroke"
                onClick={undoLastStroke}
                style={{
                  ...circleButtonStyle,
                  width: "50px",
                  height: "50px",
                  fontSize: "24px",
                  background: "#f3e1b2",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  src="/undo.png"
                  alt="Undo"
                  style={{
                    width: "25px",
                    height: "25px",
                    objectFit: "contain",
                  }}
                />
              </button>
              <button
                title="Clear All"
                onClick={handleClearCanvas}
                style={{
                  ...circleButtonStyle,
                  width: "50px",
                  height: "50px",
                  fontSize: "24px",
                  background: "#ff9999",
                }}
              >
                🗑️
              </button>
            </div>
          )}
        </div>

        {/* Replace the Answer input with AnswerBox component */}
        <AnswerBox
          userAnswer={userAnswer}
          setUserAnswer={setUserAnswer}
          handleAnswerSubmit={handleAnswerSubmit}
          wrongAttempts={wrongAttempts}
          isWrongAnswer={isWrongAnswer}
        />

        {/* Replace the Hint Button with Links Avatar */}
        <div
          style={{
            position: "fixed",
            left: "20px",
            bottom: "20px",
            zIndex: 2,
            animation:
              isHintButtonShaking &&
              !hintActive &&
              !isClarificationPlaying &&
              !isHintLoading
                ? "rotationalShake 1.5s ease-in-out infinite"
                : "none",
            transformOrigin: "center", // Set rotation origin to bottom center
          }}
        >
          <LinksAvatar
            onClick={() => {
              resetInactivityTimer();
              requestHint();
            }}
            isProcessing={isHintLoading}
            wrongAttempts={wrongAttempts}
            streakCount={streakCount}
            isCorrectAnswer={isCorrectAnswer}
            disabled={hintActive || isClarificationPlaying || isHintLoading}
          />
        </div>

        {/* Show trial counter when in trial mode */}
        {isTrialMode && (
          <div
            style={{
              position: "fixed",
              top: "20px",
              right: "20px",
              zIndex: 10,
              backgroundColor: "#f3e1b2",
              padding: "10px 15px",
              borderRadius: "12px",
              fontWeight: "600",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <div style={{ fontSize: "14px", marginBottom: "4px" }}>
              Free Trial
            </div>
            <div style={{ fontSize: "18px" }}>
              {trialProblemsCount}/5 Problems
            </div>
          </div>
        )}

        {/* Show welcome banner for trial mode */}
        {isTrialMode && <WelcomeBanner />}

        {/* Show trial modal when completed */}
        {showTrialModal && <TrialModal />}

        {/* Add GeldTransaction for animations */}
        <GeldTransaction />

        {/* Clear Confirmation Modal */}
        {showClearConfirmation && (
          <ConfirmationModal
            message="Are you sure you want to clear your work?"
            onConfirm={confirmClearCanvas}
            onCancel={cancelClearCanvas}
          />
        )}

        {/* Expanded Progress Bar Modal */}
        {isProgressExpanded && (
          <ExpandedProgressBar
            progress={unitProgress}
            unitNumber={unitNumber}
            onClose={handleCloseExpanded}
          />
        )}
      </div>
    </BorderLayout>
  );
}

export default MathApp;
